// Wallet address validation utilities for different cryptocurrencies

interface ValidationResult {
  isValid: boolean;
  message?: string;
  example?: string;
}

interface AddressRule {
  pattern: RegExp;
  minLength: number;
  maxLength: number;
  example: string;
  description: string;
}

// Address validation rules for major cryptocurrencies
const ADDRESS_RULES: { [key: string]: AddressRule } = {
  // Bitcoin (Legacy, SegWit, Bech32)
  btc: {
    pattern: /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}$/,
    minLength: 26,
    maxLength: 42,
    example: "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh",
    description: "Bitcoin address (starts with 1, 3, or bc1)"
  },

  // Ethereum and ERC-20 tokens (USDT, USDC, etc.)
  eth: {
    pattern: /^0x[a-fA-F0-9]{40}$/,
    minLength: 42,
    maxLength: 42,
    example: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    description: "Ethereum address (starts with 0x)"
  },

  // USDT (ERC-20)
  usdterc20: {
    pattern: /^0x[a-fA-F0-9]{40}$/,
    minLength: 42,
    maxLength: 42,
    example: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    description: "USDT (ERC-20) Ethereum address"
  },

  // USDC (ERC-20)
  usdc: {
    pattern: /^0x[a-fA-F0-9]{40}$/,
    minLength: 42,
    maxLength: 42,
    example: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    description: "USDC (ERC-20) Ethereum address"
  },

  // BNB Smart Chain (BEP-20)
  bnbbsc: {
    pattern: /^0x[a-fA-F0-9]{40}$/,
    minLength: 42,
    maxLength: 42,
    example: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    description: "BNB Smart Chain address (starts with 0x)"
  },

  // USDT (BEP-20)
  usdtbsc: {
    pattern: /^0x[a-fA-F0-9]{40}$/,
    minLength: 42,
    maxLength: 42,
    example: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    description: "USDT (BEP-20) BSC address"
  },

  // USDT (TRC-20)
  usdttrc20: {
    pattern: /^T[A-Za-z1-9]{33}$/,
    minLength: 34,
    maxLength: 34,
    example: "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t",
    description: "USDT (TRC-20) TRON address (starts with T)"
  },

  // TRON
  trx: {
    pattern: /^T[A-Za-z1-9]{33}$/,
    minLength: 34,
    maxLength: 34,
    example: "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t",
    description: "TRON address (starts with T)"
  },

  // Solana
  sol: {
    pattern: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
    minLength: 32,
    maxLength: 44,
    example: "7GCihgDB8fe6KNjn2MYtkzZcRjQy3t9GHdC8uHYmW2hr",
    description: "Solana address"
  },

  // USDT (Solana)
  usdtsol: {
    pattern: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
    minLength: 32,
    maxLength: 44,
    example: "7GCihgDB8fe6KNjn2MYtkzZcRjQy3t9GHdC8uHYmW2hr",
    description: "USDT (Solana) address"
  },

  // Polygon (MATIC)
  matic: {
    pattern: /^0x[a-fA-F0-9]{40}$/,
    minLength: 42,
    maxLength: 42,
    example: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    description: "Polygon address (starts with 0x)"
  },

  // USDT (Polygon)
  usdtmatic: {
    pattern: /^0x[a-fA-F0-9]{40}$/,
    minLength: 42,
    maxLength: 42,
    example: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    description: "USDT (Polygon) address"
  },

  // Ripple (XRP)
  xrp: {
    pattern: /^r[0-9a-zA-Z]{24,34}$/,
    minLength: 25,
    maxLength: 35,
    example: "rLNaPoKeeBjZe2qs6x52yVPZpZ8td4dc6w",
    description: "Ripple address (starts with r)"
  },

  // Cardano (ADA)
  ada: {
    pattern: /^addr1[0-9a-zA-Z]{58,98}$/,
    minLength: 63,
    maxLength: 103,
    example: "addr1q8d8a6v4j5k0c8z9x7y6w5v4u3i2o1p0q9r8s7t6u5i4o3p2q1",
    description: "Cardano address (starts with addr1)"
  },

  // Litecoin
  ltc: {
    pattern: /^(L|M|3)[a-zA-HJ-NP-Z0-9]{26,41}$/,
    minLength: 27,
    maxLength: 44,
    example: "Ld9ARcTTGhjDkEf7d8kV3bKpRqKw9nXQzC",
    description: "Litecoin address (starts with L, M, or 3)"
  },

  // Dogecoin
  doge: {
    pattern: /^D[5-9A-HJ-NP-U][1-9A-HJ-NP-Za-km-z]{32}$/,
    minLength: 34,
    maxLength: 34,
    example: "D7Z5tX4vUWnQYRmYy3T3B3D3B3D3B3D3B3D3B3D3B",
    description: "Dogecoin address (starts with D)"
  },

  // Polygon USDC
  usdcmatic: {
    pattern: /^0x[a-fA-F0-9]{40}$/,
    minLength: 42,
    maxLength: 42,
    example: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    description: "USDC (Polygon) address"
  },

  // Monero (XMR)
  xmr: {
    pattern: /^[48][1-9A-HJ-NP-Za-km-z]{94,}$/,
    minLength: 95,
    maxLength: 120,
    example: "4AdUndXHHZ6cfufTMvppY6JwXNouMBzSkbLYfpAV5Usx3skQNBYBXW...",
    description: "Monero address (starts with 4 or 8)"
  }
};

// Default validation rule for unknown currencies
const DEFAULT_RULE: AddressRule = {
  pattern: /^[a-zA-Z0-9]{20,120}$/,
  minLength: 20,
  maxLength: 120,
  example: "Address format varies by currency",
  description: "Cryptocurrency address"
};

// Address format is determined by the NETWORK, not the token.
// USDT on ETH, BSC, ARB all use 0x...; USDT on TRX uses T...; etc.
const NETWORK_RULE_MAP: Record<string, AddressRule> = {
  eth: ADDRESS_RULES.eth,
  bsc: ADDRESS_RULES.bnbbsc,
  arb: ADDRESS_RULES.eth,
  arbitrum: ADDRESS_RULES.eth,
  matic: ADDRESS_RULES.matic,
  polygon: ADDRESS_RULES.matic,
  op: ADDRESS_RULES.eth,
  optimism: ADDRESS_RULES.eth,
  avax: ADDRESS_RULES.eth,
  base: ADDRESS_RULES.eth,
  ftm: ADDRESS_RULES.eth,
  fantom: ADDRESS_RULES.eth,
  trx: ADDRESS_RULES.trx,
  tron: ADDRESS_RULES.trx,
  trc20: ADDRESS_RULES.trx,
  sol: ADDRESS_RULES.sol,
  solana: ADDRESS_RULES.sol,
};

const EVM_NETWORKS = new Set(['eth', 'bsc', 'arb', 'arbitrum', 'matic', 'polygon', 'op', 'optimism', 'avax', 'base', 'ftm', 'fantom']);
const TRON_NETWORKS = new Set(['trx', 'tron', 'trc20']);
const SOL_NETWORKS = new Set(['sol', 'solana']);

const resolveAddressRule = (currency: string): AddressRule => {
  const key = currency.toLowerCase();
  if (ADDRESS_RULES[key]) return ADDRESS_RULES[key];

  const underscore = key.lastIndexOf('_');
  if (underscore > 0) {
    const network = key.slice(underscore + 1);
    if (NETWORK_RULE_MAP[network]) return NETWORK_RULE_MAP[network];

    const baseTicker = key.slice(0, underscore);
    if (ADDRESS_RULES[baseTicker]) return ADDRESS_RULES[baseTicker];
  }

  return DEFAULT_RULE;
};

const resolveNetworkFamily = (currency: string): 'evm' | 'tron' | 'solana' | null => {
  const key = currency.toLowerCase();
  if (EVM_NETWORKS.has(key) || TRON_NETWORKS.has(key) || SOL_NETWORKS.has(key)) {
    if (EVM_NETWORKS.has(key)) return 'evm';
    if (TRON_NETWORKS.has(key)) return 'tron';
    return 'solana';
  }
  const underscore = key.lastIndexOf('_');
  if (underscore > 0) {
    const network = key.slice(underscore + 1);
    if (EVM_NETWORKS.has(network)) return 'evm';
    if (TRON_NETWORKS.has(network)) return 'tron';
    if (SOL_NETWORKS.has(network)) return 'solana';
  }
  return null;
};

/**
 * Validates a wallet address based on currency-specific rules
 * @param address The wallet address to validate
 * @param currency The cryptocurrency ticker (lowercase)
 * @returns ValidationResult with validation status and message
 */
export const validateWalletAddress = (address: string, currency: string): ValidationResult => {
  if (!address || address.trim().length === 0) {
    return {
      isValid: false,
      message: 'Wallet address is required'
    };
  }

  // Clean the address
  const cleanAddress = address.trim();

  // Get the validation rule for this currency
  const rule = resolveAddressRule(currency);

  // Check length
  if (cleanAddress.length < rule.minLength || cleanAddress.length > rule.maxLength) {
    return {
      isValid: false,
      message: `Invalid address length. Expected ${rule.minLength}-${rule.maxLength} characters.`,
      example: rule.example
    };
  }

  // Check format with regex
  if (!rule.pattern.test(cleanAddress)) {
    return {
      isValid: false,
      message: `Invalid ${rule.description} format.`,
      example: rule.example
    };
  }

  // Check for common invalid patterns
  if (cleanAddress.toLowerCase().includes('invalid') ||
      cleanAddress.toLowerCase().includes('example') ||
      cleanAddress.toLowerCase().includes('test') ||
      cleanAddress.toLowerCase().includes('placeholder')) {
    return {
      isValid: false,
      message: 'Please enter a real wallet address, not a placeholder.',
      example: rule.example
    };
  }

  return {
    isValid: true,
    message: 'Valid wallet address'
  };
};

/**
 * Gets the address validation rule for a currency
 * @param currency The cryptocurrency ticker (lowercase)
 * @returns AddressRule for the currency
 */
export const getAddressRule = (currency: string): AddressRule => {
  return resolveAddressRule(currency);
};

/**
 * Gets a human-readable description of the expected address format
 * @param currency The cryptocurrency ticker (lowercase)
 * @returns Formatted string with address requirements
 */
export const getAddressRequirements = (currency: string): string => {
  const rule = getAddressRule(currency);
  return `${rule.description}. Format: ${rule.example}`;
};

/**
 * Lists all supported currencies for validation
 * @returns Array of supported currency tickers
 */
export const getSupportedCurrencies = (): string[] => {
  return Object.keys(ADDRESS_RULES);
};

/**
 * Enhanced validation for common user mistakes
 * @param address The wallet address to validate
 * @param currency The cryptocurrency ticker (lowercase)
 * @returns ValidationResult with detailed feedback
 */
export const enhancedAddressValidation = (address: string, currency: string): ValidationResult => {
  const basicValidation = validateWalletAddress(address, currency);

  if (!basicValidation.isValid) {
    return basicValidation;
  }

  // Additional checks for common mistakes
  const cleanAddress = address.trim();

  // Check for mixed case in case-sensitive addresses
  if (currency.toLowerCase() === 'btc' && cleanAddress !== cleanAddress.toLowerCase()) {
    // Bitcoin addresses can be mixed case, but let's warn about it
    return {
      isValid: true,
      message: 'Valid Bitcoin address (case-sensitive)',
      example: ADDRESS_RULES.btc.example
    };
  }

  // Check for Ethereum checksum (simplified) — applies to any EVM-network currency
  if (resolveNetworkFamily(currency) === 'evm') {
    if (!cleanAddress.startsWith('0x')) {
      return {
        isValid: false,
        message: 'EVM addresses must start with "0x"',
        example: ADDRESS_RULES.eth.example
      };
    }
  }

  // Check for TRON address format — applies to any TRON-network currency
  if (resolveNetworkFamily(currency) === 'tron') {
    if (!cleanAddress.startsWith('T')) {
      return {
        isValid: false,
        message: 'TRON addresses must start with "T"',
        example: ADDRESS_RULES.trx.example
      };
    }
  }

  return {
    isValid: true,
    message: 'Valid wallet address'
  };
};